import { makeStyles, Typography } from '@material-ui/core';
import React, { useContext } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import { Step1, Step2, Step3 } from 'components/Forms/CharacterizationForm';
import MultiStepForm from 'components/Forms/MultiStepForm';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { Routes } from 'routes';
import { putCharacterization } from 'services/apiOperations';
import { CharacterizationForm, CharacterizationMaterial, CharacterizationSubMaterial } from 'types';
import { formatCharacterizationBeforePut } from 'utils/normalizer';
import { useCharacterizationById } from 'hooks';
import Loading from 'components/Loading';
import { goBack } from 'utils/history';

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  title: {
    margin: 0,
  },
});

const CharacterizationEdit: React.FC = () => {
  const context = useContext(AppContext);
  const history = useHistory();
  const location = useLocation<{ referer: string } | undefined>();
  const classes = useStyles();
  const { t } = useTranslation();
  const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;

  const { characterizationId } = useParams<{ characterizationId: string }>();
  const { loading, value: characterization } = useCharacterizationById(characterizationId);

  const validReferers = [
    Routes.Characterizations,
    Routes.CharacterizationShow.replace(':characterizationId', characterizationId),
    Routes.CharacterizationEdit.replace(':characterizationId', characterizationId),
  ];

  const backRoute =
    !!location.state && validReferers.includes(location.state.referer)
      ? location.state.referer.replace('/edit', '')
      : Routes.Characterizations;

  const onSubmit: SubmitHandler<CharacterizationForm> = async (characterization: CharacterizationForm) => {
    if (null === sortingCenterId) {
      notify({
        message: t('characterizationForm.modify.error'),
        severity: 'error',
      });
      return;
    }
    let formattedCharacterization = formatCharacterizationBeforePut(characterization, sortingCenterId);
    return putCharacterization(formattedCharacterization, characterizationId)
      .then(() => {
        notify({
          message: t('characterizationForm.modify.success'),
          severity: 'success',
        });
        goBack(backRoute, history);
      })
      .catch(() => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };
  const steps = [
    {
      label: t('characterizationForm.steps.step1'),
      component: Step1,
      requiredFieldsLegend: true,
      nextButtonLabel: t('characterizationForm.modify.nextStepLabel'),
    },
    {
      label: t('characterizationForm.steps.step2'),
      component: Step2,
      nextButtonLabel: t('characterizationForm.modify.nextStepLabel'),
    },
    {
      label: t('characterizationForm.steps.step3'),
      component: Step3,
      nextButtonLabel: t('characterizationForm.modify.saveModificationLabel'),
    },
  ];

  return (
    <>
      <div className={classes.headerTitle}>
        <Typography variant="h1" className={classes.title}>
          {t('characterizationForm.modify.title')}
        </Typography>
      </div>
      {loading || !characterization ? (
        <Loading />
      ) : (
        <MultiStepForm
          formName="characterizationForm"
          backRoute={backRoute}
          backIcon={<ArrowBackIcon />}
          onSubmit={onSubmit}
          steps={steps}
          initialData={{
            id: characterization['@id'],
            samplingDate: new Date(characterization.samplingDate),
            analyseDate: characterization.analyseDate ? new Date(characterization.analyseDate) : null,
            client: characterization.client['@id'],
            stream: characterization.stream['@id'],
            comment: characterization.comment,
            secteur: characterization.secteur,
            characterizationMaterials: characterization.characterizationMaterials.map(
              (characMaterial: CharacterizationMaterial) => ({
                material: characMaterial.material['@id'],
                weight: characMaterial.weight / 1000,
                characterizationSubMaterials: characMaterial.characterizationSubMaterials
                  ?.sort((a: CharacterizationSubMaterial, b: CharacterizationSubMaterial) =>
                    a.subMaterial.name.localeCompare(b.subMaterial.name),
                  )
                  .map((characSubMaterial: CharacterizationSubMaterial) => ({
                    '@id': characSubMaterial['@id'],
                    weight: null === characSubMaterial.weight ? '' : (characSubMaterial.weight / 1000).toString(),
                    subMaterial: characSubMaterial.subMaterial['@id'],
                    sortingCenter: characSubMaterial.sortingCenter['@id'],
                  })),
              }),
            ),
            weight: characterization.weight / 1000,
          }}
          initialContext={{
            client: characterization.client,
            stream: characterization.stream,
            isEditing: true,
          }}
        />
      )}
    </>
  );
};
export default CharacterizationEdit;
